@if (displayAccountToast()) {
  <div id="toast">
    <app-toast
      toastStyle="positive"
      icon="checkmark"
      [text]="accountToastMessage()"
    ></app-toast>
  </div>
}
@if (isLoading()) {
  <div class="loading-spinner--container">
    <app-loading-spinner></app-loading-spinner>
  </div>
} @else if (!getUserError()) {
  <div id="title--container">
    <h4>Edit User</h4>
    <h5>{{ laboratory.laboratoryName }}</h5>
  </div>
  <hr />
  <div id="account--container">
    <div class="card details">
      <div class="header">
        <div class="card-title">User Details</div>
        <div class="body1">Update the user's name and role</div>
      </div>
      <hr class="card-divider" aria-hidden="true" />
      <div class="content">
        <div class="form--container">
          <form [formGroup]="editUserForm">
            <app-underlined-form-field
              [parentForm]="editUserForm"
              [field]="firstNameField"
            ></app-underlined-form-field>
            <app-underlined-form-field
              [parentForm]="editUserForm"
              [field]="lastNameField"
            ></app-underlined-form-field>
            <app-underlined-dropdown-form-field
              [field]="userRoleField"
              [parentForm]="editUserForm"
            ></app-underlined-dropdown-form-field>
          </form>
          <div class="button--container">
            <app-button
              label="Save changes"
              buttonId="update-user-btn"
              (buttonClicked)="onSubmit(false)"
              [isDisabled]="editUserForm.invalid || !detailsModified"
              [isProcessing]="updateUserDetailsProcessing()"
            >
            </app-button>
          </div>
        </div>
      </div>
    </div>
    <div class="card">
      <div class="header">
        <div class="card-title">Laboratory Memberships</div>
        <div class="body1">Assign this user to one or more laboratories</div>
      </div>
      <hr class="card-divider" aria-hidden="true" />
      <div class="content">
        @if (availableLabsField.options.length > 0) {
          <div class="form--container">
            <form [formGroup]="editUserForm">
              <app-underlined-dropdown-form-field
                [field]="availableLabsField"
                [parentForm]="editUserForm"
              ></app-underlined-dropdown-form-field>
            </form>
          </div>
        } @else if (user()?.laboratories?.length > 0) {
          <div class="no-items-message">All available laboratories have been assigned.</div>
        }
        @if (user()?.laboratories?.length < 1 && !labSelectError()) {
          <div class="no-items-message">User not assigned to any laboratories.</div>
        }
        @if (labSelectError()) {
          <div class="error"> {{ labSelectError() }} </div>
        }
        @if (user()?.laboratories?.length > 0) {
          @for (lab of user()?.laboratories; track lab?.laboratoryId) {
            <app-lab-card
              [lab]="lab"
              [role]="editUserForm.get(userRoleField.key)?.value"
              (removeClicked)="removeLab($event)"
            ></app-lab-card>
          }
        }
        <div class="button--container">
          <app-button
            label="Save changes"
            buttonId="update-user-btn"
            (buttonClicked)="onSubmit(true)"
            [isDisabled]="editUserForm.invalid || !labsModified()"
            [isProcessing]="updateLabsProcessing()"
          >
          </app-button>
        </div>
      </div>
    </div>
    <div class="card">
      <div class="header">
        <div class="card-title">Account Status</div>
        <div class="body1">Control whether this user can sign in</div>
      </div>
      <hr class="card-divider" aria-hidden="true" />
      <div class="content">
        <form class="account-disabled" [formGroup]="disableAccountForm">
          <div>
            <div class="subtitle2">Account Disabled</div>
            <div class="body1">
              Disabling prevents this user from logging in without deleting
              their data.
            </div>
          </div>
          <mat-slide-toggle
            [id]="disableAccountKey"
            [formControlName]="disableAccountKey"
            aria-label="Account disabled"
            ngDefaultControl
            color="primary"
          ></mat-slide-toggle>
        </form>
        @if (user()?.isLockedOut) {
          <div class="account-locked">
            <div class="settings-title">
              <div class="subtitle2">
                <mat-icon class="warning" aria-hidden="true">lock</mat-icon
                >Account Locked
              </div>
              <div class="body1">User account is locked.</div>
            </div>
            <div class="button--container">
              <app-button
                label="Unlock account"
                buttonId="unlock-account-btn"
                (buttonClicked)="unlockAccount()"
                [isProcessing]="unlockAccountProcessing()"
              >
              </app-button>
            </div>
          </div>
        }
      </div>
    </div>
    @if (currentUser?.role | roleCheck: superAdminRoles) {
      <div class="action-card--container">
        @if (user()?.twoFactorEnabled) {
          <app-button-card
            cardStyle="warning"
            title="Two-Factor Authentication"
            bodyLine1="Click to reset and re-enroll this user's two-factor authentication."
            bodyLine2="A confirmation email will be sent to the user."
            buttonLabel="Reset"
            buttonId="reset-2fa-btn"
            iconName="refresh"
            (buttonClicked)="openModal('reset-2fa')"
          ></app-button-card>
        }
        <app-button-card
          id="delete-user-card"
          cardStyle="negative"
          title="Delete this user"
          bodyLine1="Permanently removes this user account & all associated data."
          bodyLine2="This action cannot be undone."
          buttonLabel="Delete user"
          buttonId="delete-user-btn"
          iconName="delete"
          (buttonClicked)="openModal('delete-user')"
        ></app-button-card>
      </div>
    }
  </div>
} @else {
  <app-error-page [primaryText]="getUserError()?.message"></app-error-page>
}
<app-modal
  id="success"
  titleText="Success"
  [primaryText]="updateUserSuccessMessage()"
  primaryButtonText="close"
  primaryButtonId="update-user-button"
  modalStyle="success"
  (modalButtonClicked)="modalButtonClicked($event)"
></app-modal>
<app-modal
  id="error"
  titleText="Error"
  [primaryText]="error()?.message"
  primaryButtonText="close"
  primaryButtonId="error-button"
  modalStyle="error"
  (modalButtonClicked)="modalButtonClicked($event)"
></app-modal>
<app-modal
  id="attention"
  titleText="Attention"
  primaryText="Your changes have not been saved. Press cancel to return to the form and save your changes, or continue to discard."
  buttonLayout="double-button"
  primaryButtonText="Continue"
  primaryButtonId="confirm-navigation"
  secondaryButtonText="Cancel"
  secondaryButtonId="cancel-button"
  modalStyle="warning"
  (modalButtonClicked)="modalButtonClicked($event)"
></app-modal>
<app-validation-modal
  id="delete-user"
  titleText="Delete user"
  validationText="delete"
  primaryButtonText="Delete"
  primaryButtonId="primary-button"
  secondaryButtonText="Close"
  secondaryButtonId="secondary-button"
  (modalButtonClicked)="deleteUser()"
  [error]="error()"
></app-validation-modal>
<app-validation-modal
  id="reset-2fa"
  titleText="Reset Two-Factor Authentication"
  validationText="reset"
  primaryButtonText="Reset"
  primaryButtonId="primary-button"
  secondaryButtonText="Close"
  secondaryButtonId="secondary-button"
  (modalButtonClicked)="resetTwoFactorAuthentication()"
  [isProcessing]="reset2FAProcessing()"
  [error]="error()"
></app-validation-modal>
<app-validation-modal
  id="disable-user"
  titleText="Disable user"
  validationText="disable"
  primaryButtonText="Disable"
  primaryButtonId="primary-button"
  secondaryButtonText="Close"
  secondaryButtonId="secondary-button"
  (modalButtonClicked)="disableAccount(true)"
  [isProcessing]="disableAccountProcessing()"
  [error]="error()"
></app-validation-modal>
<app-modal
  id="re-enable-user"
  titleText="Re-enable user"
  primaryText="This user will be able to log in again."
  buttonLayout="double-button"
  primaryButtonText="Re-enable"
  primaryButtonId="re-enable-confirm"
  secondaryButtonText="Cancel"
  secondaryButtonId="re-enable-cancel"
  modalStyle="warning"
  (modalButtonClicked)="modalButtonClicked($event)"
></app-modal>
